import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import CategoryFilter from '../../components/ui/CategoryFilter';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const Downloads = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [downloads, setDownloads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/sign-in');
      return;
    }

    if (user) {
      fetchDownloads();
    }
  }, [user, authLoading, navigate]);

  const fetchDownloads = async () => {
    try {
      const { data, error } = await supabase?.from('downloads')?.select(`
          *,
          ebook:ebooks(*),
          purchase:purchases(*)
        `)?.eq('user_id', user?.id)?.order('created_at', { ascending: false });

      if (error) throw error;
      setDownloads(data || []);
    } catch (error) {
      console.error('Error fetching downloads:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (downloadId, ebookTitle) => {
    try {
      const { error } = await supabase?.from('downloads')?.update({
          download_count: supabase?.raw('download_count + 1'),
          last_downloaded_at: new Date()?.toISOString(),
        })?.eq('id', downloadId);

      if (error) throw error;

      alert(`Downloading: ${ebookTitle}\n\nIn a production environment, this would download the actual eBook file.`);
      fetchDownloads();
    } catch (error) {
      console.error('Error updating download count:', error);
    }
  };

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <CategoryFilter />
        <main className="pt-32 pb-12">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex items-center justify-center py-12">
              <Icon name="Loader2" size={32} className="animate-spin text-primary" />
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>My Downloads - StrategyShelf</title>
        <meta name="description" content="Access and download your purchased eBooks from StrategyShelf." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <CategoryFilter />

        <main className="pt-32 pb-12 md:pb-16 lg:pb-20">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="mb-6">
              <button
                onClick={() => navigate('/account')}
                className="flex items-center gap-2 text-sm md:text-base text-muted-foreground hover:text-foreground transition-smooth focus-ring rounded-md"
              >
                <Icon name="ArrowLeft" size={20} />
                <span>Back to Account</span>
              </button>
            </div>

            <h1 className="text-3xl md:text-4xl font-heading font-semibold text-foreground mb-6 md:mb-8">
              My Downloads
            </h1>

            {downloads?.length === 0 ? (
              <div className="bg-card rounded-lg border border-border p-12 text-center">
                <Icon name="Download" size={64} className="text-muted-foreground mx-auto mb-4" />
                <h2 className="text-xl font-heading font-semibold text-foreground mb-2">
                  No Downloads Yet
                </h2>
                <p className="text-muted-foreground mb-6">
                  Purchase eBooks to access them here
                </p>
                <Button
                  variant="default"
                  iconName="BookOpen"
                  iconPosition="left"
                  onClick={() => navigate('/e-book-catalog')}
                >
                  Browse eBooks
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {downloads?.map((download) => (
                  <div
                    key={download?.id}
                    className="bg-card rounded-lg border border-border overflow-hidden hover:shadow-lg transition-smooth"
                  >
                    <div className="aspect-[3/4] relative">
                      <img
                        src={download?.ebook?.cover_image}
                        alt={download?.ebook?.cover_image_alt}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-2 right-2 bg-success text-success-foreground px-2 py-1 rounded-md text-xs font-medium">
                        Purchased
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading font-semibold text-foreground mb-1 line-clamp-2">
                        {download?.ebook?.title}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-3">
                        by {download?.ebook?.author}
                      </p>

                      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4">
                        <Icon name="Download" size={14} />
                        <span>
                          Downloaded {download?.download_count || 0} times
                        </span>
                      </div>

                      <Button
                        variant="default"
                        fullWidth
                        iconName="Download"
                        iconPosition="left"
                        onClick={() => handleDownload(download?.id, download?.ebook?.title)}
                      >
                        Download
                      </Button>

                      {download?.ebook?.external_link && (
                        <Button
                          variant="outline"
                          fullWidth
                          iconName="ExternalLink"
                          iconPosition="left"
                          onClick={() => window.open(download?.ebook?.external_link, '_blank')}
                          className="mt-2"
                        >
                          View Online
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default Downloads;